import React from 'react';
import { StatusPill } from '../systemKit';
import { fmtCompact, fmtFull } from './utils';

// TopSpend — purely presentational. Renders a ranked list of spend rows.
// Owner of this component is responsible for fetching rows (via the
// cost_dashboard_top_spend RPC) and providing them as a `rows` prop.
//
// Pre-restructure this component fetched its own data so it could live
// inside the Cost Dashboard's section composition. The Cost Dashboard
// no longer carries this section — it lives in the Reports tab on the
// Invoices page. Decoupling makes the Reports tab's filter/sort flow
// straightforward (parent fetches once, applies vendor/polarity/sort,
// passes filtered rows in).

export default function TopSpend({
  rows,
  loading,
  onOpenDrillIn,
  onAddToWatchlist,
  capNoticeText,
}) {
  return (
    <div>
      {loading && <Skeleton />}
      {!loading && rows && rows.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          No spend recorded in this window. Try a different time range or wait for your next invoice.
        </div>
      )}
      {!loading && rows && rows.length > 0 && (
        <>
          <div style={{ borderTop: '1px solid var(--border)' }}>
            {rows.map((r, idx) => (
              <SpendRow
                key={r.food_item_id}
                rank={idx + 1}
                row={r}
                onOpen={() => onOpenDrillIn?.(r)}
                onAdd={() => onAddToWatchlist?.(r.food_item_id)}
              />
            ))}
          </div>
          {capNoticeText && (
            <div style={{ padding: 10, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
              {capNoticeText}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SpendRow({ rank, row, onOpen, onAdd }) {
  return (
    <div
      onClick={onOpen}
      style={{
        display: 'grid',
        gridTemplateColumns: '32px minmax(0, 2fr) 110px 80px 110px 130px',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
        transition: 'background 120ms ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
        {rank}
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.item_name || `Item #${row.food_item_id}`}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {(row.primary_vendor || '—').replace(/\n/g, ' · ')}
        </div>
      </div>

      <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
          {fmtCompact(row.total_spend)}
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          total
        </div>
      </div>

      <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
        <div>{row.invoice_count}</div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>invoices</div>
      </div>

      <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
        <div style={{ color: 'var(--text-muted)', fontSize: 10 }}>avg → latest</div>
        <div>
          {row.avg_unit_price != null ? fmtFull(row.avg_unit_price) : '—'}
          {' → '}
          <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
            {row.latest_unit_price != null ? fmtFull(row.latest_unit_price) : '—'}
          </span>
        </div>
      </div>

      <div style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
        {row.on_watchlist ? (
          <StatusPill tone="purple">On watchlist</StatusPill>
        ) : (
          <button
            onClick={onAdd}
            style={{
              padding: '4px 10px',
              fontSize: 11, fontWeight: 600,
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 6,
              color: 'var(--text-primary)',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            + Watchlist
          </button>
        )}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div style={{ borderTop: '1px solid var(--border)' }}>
      {[0, 1, 2, 3, 4].map(i => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '32px 2fr 110px 80px 110px 130px',
          gap: 12, padding: '14px 16px',
          borderBottom: '1px solid var(--border)',
        }}>
          {[0, 1, 2, 3, 4, 5].map(j => (
            <div key={j} style={{ height: 12, background: 'var(--bg-subtle)', borderRadius: 4 }} />
          ))}
        </div>
      ))}
    </div>
  );
}
